import {PagedData} from "@/services/models/shared/PagedData";
import {AxiosResponse} from "axios";
import {
    AzureBillingAdjustment,
    AzureBillingCharge,
    AzureBillingPeriodFilterOptions,
    AzureBillingPeriodListItem,
    AzureBillingPeriodSubscription,
    NewAzureBillingAdjustment,
    UpdateSubscription
} from "@/services/models/AzureBilling";
import FileProvider from "@/utilities/providers/FileProvider";
import BimsHttpService from "@/services/BimsHttpService";

class AzureBillingService {
    /**
     * @description Returns a paged list of azure billing periods.
     */
    getBillingPeriodsList(filter: AzureBillingPeriodFilterOptions): Promise<PagedData<AzureBillingPeriodListItem>> {
        return BimsHttpService.get(`/azureBillingPeriods`, {params: filter}).then((response: AxiosResponse<PagedData<AzureBillingPeriodListItem>>) => {
            return response.data as PagedData<AzureBillingPeriodListItem>;
        });
    }

    /**
     * @description Downloads the azure billing invoice pdf for the given billing period
     */
    public downloadInvoice(billingPeriodId: number): Promise<any> {
        return BimsHttpService.get(
            `/azureBillingPeriods/${billingPeriodId}/export`, {
                responseType: 'blob'
            }).then(response => {
            return FileProvider.ForceFileDownload(response)
        })
    }

    /**
     * @description Returns all subscriptions for the given billing period id
     */
    getBillingPeriodSubscriptions(billingPeriodId: number): Promise<AzureBillingPeriodSubscription[]> {
        return BimsHttpService.get(`/azureBillingPeriods/${billingPeriodId}/subscriptions`).then((response: AxiosResponse<AzureBillingPeriodSubscription[]>) => {
            return response.data as AzureBillingPeriodSubscription[];
        });
    }

    /**
     * @description Returns all subscriptions for the given billing period id
     */
    getSubscription(billingPeriodId: number, subscriptionId: string): Promise<AzureBillingPeriodSubscription> {
        return BimsHttpService.get(`/azureBillingPeriods/${billingPeriodId}/subscriptions/${subscriptionId}`).then((response: AxiosResponse<AzureBillingPeriodSubscription>) => {
            return response.data as AzureBillingPeriodSubscription;
        });
    }

    /**
     * @description Returns all subscriptions for the given billing period id
     */
    updateSubscription(billingPeriodId: number, subscriptionId: string, subscription: UpdateSubscription): Promise<AzureBillingPeriodSubscription> {
        return BimsHttpService.put(`/azureBillingPeriods/${billingPeriodId}/subscriptions/${subscriptionId}`, subscription).then((response: AxiosResponse<AzureBillingPeriodSubscription>) => {
            return response.data as AzureBillingPeriodSubscription;
        });
    }

    /**
     * @description Returns all subscriptions for the given billing period id
     */
    getSubscriptionCharges(billingPeriodId: number, subscriptionId: string): Promise<AzureBillingCharge[]> {
        return BimsHttpService.get(`/azureBillingPeriods/${billingPeriodId}/subscriptions/${subscriptionId}/charges`).then((response: AxiosResponse<AzureBillingCharge[]>) => {
            return response.data as AzureBillingCharge[];
        });
    }

    /**
     * @description Returns all subscriptions for the given billing period id
     */
    getSubscriptionAdjustments(billingPeriodId: number, subscriptionId: string): Promise<AzureBillingAdjustment[]> {
        return BimsHttpService.get(`/azureBillingPeriods/${billingPeriodId}/subscriptions/${subscriptionId}/adjustments`).then((response: AxiosResponse<AzureBillingAdjustment[]>) => {
            return response.data as AzureBillingAdjustment[];
        });
    }

    /**
     * @description Returns all subscriptions for the given billing period id
     */
    addSubscriptionAdjustment(billingPeriodId: number, subscriptionId: string, adjustment: NewAzureBillingAdjustment): Promise<any> {
        return BimsHttpService.post(`/azureBillingPeriods/${billingPeriodId}/subscriptions/${subscriptionId}/adjustments`, adjustment).then((response: AxiosResponse<any>) => {
            return response.data;
        });
    }

    /**
     * @description Returns all subscriptions for the given billing period id
     */
    deleteSubscriptionAdjustment(id: string): Promise<void> {
        return BimsHttpService.delete(`/azureBillingPeriods/subscriptions/adjustments/${id}`);
    }
}

export default new AzureBillingService()
